'use client';
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import debounce from 'lodash/debounce';
import { RolexImage } from '@/utils/RolexImage';
import {
  SearchResult,
  SearchSymbolResultItem,
  ExtendedSearchSymbolResultItem,
} from '../dashboard/watchlist/watchlist.types';

// Enhanced interface for ISimpleDatafeed
interface ISimpleDatafeed {
  searchSymbols?: (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResult: (result: SearchResult[]) => void
  ) => void;
}

// Simplified constants - removed animation timing constants
const INITIAL_PLACEHOLDER = 'Search for a watch or model...';
const EXACT_RESULTS = 4;
const DURATION_LABEL = '30D';

// Moved to outside component - only needs to be defined once
const isWatchSymbol = (symbol: string): boolean => {
  const excludedPrefixes = [
    'INFLATION:',
    'INTEREST:',
    'INDEX:',
    'METAL:',
    'EMPLOYMENT:',
    'MACRO:',
    'FED:',
    'MONEY:',
    'ECONOMIC:',
  ];
  return !excludedPrefixes.some((prefix) =>
    symbol.toUpperCase().startsWith(prefix.toUpperCase())
  );
};

interface HeroSearchProps {
  onSymbolSelect: (result: SearchResult) => void;
  datafeed: ISimpleDatafeed;
  skipInitialLoad?: boolean;
}

interface TrendInfo {
  isPositive: boolean;
  changePercent: number;
  startPrice: number;
  endPrice: number;
}

interface ChartDataPoint {
  time: number;
  price: number;
}

interface PriceInfo {
  currentPrice: number;
  lastUpdated: number;
}

// Simplified state interface - removed animation-related states
interface HeroSearchState {
  searchResults: SearchResult[];
  isSearchVisible: boolean;
  inputValue: string;
  isLoading: boolean;
}

// Define props interface for SearchResultItem
interface SearchResultItemProps {
  result: SearchResult;
  miniCharts: { [key: string]: ChartDataPoint[] };
  trends: { [key: string]: TrendInfo };
  prices: { [key: string]: PriceInfo };
  onSelect: (result: SearchResult) => void;
  getFormattedWatchName: (result: SearchResult) => string;
  getModelNumber: (symbol: string) => string;
}

// Simplified SearchResultItem component - removed animation classes
const SearchResultItem = React.memo(
  ({
    result,
    miniCharts,
    trends,
    prices,
    onSelect,
    getFormattedWatchName,
    getModelNumber,
  }: SearchResultItemProps) => {
    return (
      <li
        className="p-3 hover:bg-slate-700/30 cursor-pointer relative"
        onClick={() => onSelect(result)}
      >
        <div className="absolute top-2 left-2 bg-slate-900 text-slate-300 text-[10px] px-1 rounded-sm opacity-70">
          {DURATION_LABEL}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 flex-grow">
            <div className="flex-shrink-0">
              <RolexImage
                modelNumber={getModelNumber(result.symbol)}
                size={40}
                className="rounded-full"
              />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm text-slate-200 break-words">
                {getFormattedWatchName(result)}
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <span>{result.exchange}</span>
                {trends[result.symbol] && (
                  <span
                    className={`flex items-center gap-1 font-medium ${
                      trends[result.symbol].isPositive
                        ? 'text-emerald-400'
                        : 'text-rose-400'
                    }`}
                  >
                    {trends[result.symbol].isPositive ? (
                      <ArrowUpCircle className="w-3 h-3" />
                    ) : (
                      <ArrowDownCircle className="w-3 h-3" />
                    )}
                    {Math.abs(trends[result.symbol].changePercent).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1">
            {miniCharts[result.symbol] && (
              <div className="w-24 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={miniCharts[result.symbol]}>
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke={
                        trends[result.symbol]?.isPositive
                          ? '#34d399'
                          : '#fb7185'
                      }
                      dot={false}
                      strokeWidth={1.5}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="text-sm font-medium text-slate-200">
              {prices[result.symbol] &&
              !isNaN(prices[result.symbol].currentPrice) ? (
                `$${prices[result.symbol].currentPrice.toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true,
                  }
                )}`
              ) : (
                <div className="text-xs text-slate-400">Loading...</div>
              )}
            </div>
          </div>
        </div>
      </li>
    );
  }
);

SearchResultItem.displayName = 'SearchResultItem';

// Data fetching hook remains the same
const useWatchData = (datafeed: ISimpleDatafeed) => {
  const [miniCharts, setMiniCharts] = useState<{
    [key: string]: ChartDataPoint[];
  }>({});
  const [trends, setTrends] = useState<{ [key: string]: TrendInfo }>({});
  const [prices, setPrices] = useState<{ [key: string]: PriceInfo }>({});

  // Add a request cache to avoid duplicate API calls
  const requestCache = useRef<{ [key: string]: Promise<any> }>({});

  // MOVE calculateTrend BEFORE it's used in fetchRecentPriceDataForResults
  const calculateTrend = useCallback((data: ChartDataPoint[]): TrendInfo => {
    if (data.length < 2) {
      return { isPositive: true, changePercent: 0, startPrice: 0, endPrice: 0 };
    }
    const startPrice = data[0].price;
    const endPrice = data[data.length - 1].price;
    const changePercent = ((endPrice - startPrice) / startPrice) * 100;
    return {
      isPositive: changePercent >= 0,
      changePercent,
      startPrice,
      endPrice,
    };
  }, []);

  // Throttle fetch requests instead of making them simultaneously
  const fetchRecentPriceDataForResults = useCallback(
    async (watches: SearchResult[]) => {
      if (!watches.length) return;

      const chartData: { [key: string]: ChartDataPoint[] } = {};
      const trendData: { [key: string]: TrendInfo } = {};
      const pricesData: { [key: string]: PriceInfo } = {};

      const now = Date.now();
      const twoMonthsAgoTimestamp = now - 2 * 30 * 24 * 60 * 60 * 1000;
      const fromTimestamp = Math.floor(twoMonthsAgoTimestamp / 1000);
      const toTimestamp = Math.floor(now / 1000);

      // Process watches in batches of 2 to reduce concurrent requests
      for (let i = 0; i < watches.length; i += 2) {
        const batchWatches = watches.slice(i, i + 2);

        // Process this batch
        await Promise.all(
          batchWatches.map(async (watch) => {
            try {
              const cacheKey = `${watch.symbol}-${fromTimestamp}-${toTimestamp}`;

              // Use cached data if available
              if (!requestCache.current[cacheKey]) {
                requestCache.current[cacheKey] = fetch(
                  `/api/trading-dashboard/getBars?symbol=${watch.symbol}&from=${fromTimestamp}&to=${toTimestamp}&simple=true&source=forum`
                ).then((response) => {
                  if (!response.ok) throw new Error(response.statusText);
                  return response.json();
                });
              }

              const data = await requestCache.current[cacheKey];

              if (!Array.isArray(data) || data.length === 0) {
                console.warn(
                  `No recent price data available for ${watch.symbol}`
                );
                return null;
              }

              // Process data into points
              const validPoints: ChartDataPoint[] = [];
              for (const item of data) {
                let time = item.t || item.time;
                let price: number | null = null;

                if (typeof item.p === 'number') price = item.p;
                else if (typeof item.close === 'number') price = item.close;
                else if (typeof item.p === 'string') price = parseFloat(item.p);
                else if (typeof item.close === 'string')
                  price = parseFloat(item.close);
                else if (typeof item.open === 'number') price = item.open;

                if (time && price !== null && !isNaN(price) && price > 0) {
                  validPoints.push({ time: time * 1000, price });
                }
              }

              if (validPoints.length) {
                chartData[watch.symbol] = validPoints;
                trendData[watch.symbol] = calculateTrend(validPoints);
                pricesData[watch.symbol] = {
                  currentPrice: validPoints[validPoints.length - 1].price,
                  lastUpdated: validPoints[validPoints.length - 1].time,
                };
              }
            } catch (error) {
              console.error(
                `Error fetching recent price data for ${watch.symbol}:`,
                error
              );
            }
          })
        );

        // Wait a small amount between batches to prevent overwhelming the browser
        if (i + 2 < watches.length) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      setMiniCharts((prev) => ({ ...prev, ...chartData }));
      setTrends((prev) => ({ ...prev, ...trendData }));
      setPrices((prev) => ({ ...prev, ...pricesData }));
    },
    [calculateTrend]
  );

  const fetchWatchData = useCallback(async () => {
    try {
      const response = await fetch('/api/trading-dashboard/watchData');
      const responseJson = await response.json();
      const data = responseJson.data || responseJson; // Handle both data formats

      if (!Array.isArray(data)) {
        console.error('Expected data to be an array but received:', data);
        return [];
      }

      const watchOnlyData = data.filter((item) => isWatchSymbol(item.symbol));
      const uniqueData = Array.from(
        new Map(watchOnlyData.map((item) => [item.symbol, item])).values()
      );

      const sortedModels = uniqueData
        .sort((a, b) => (b.dataCount || 0) - (a.dataCount || 0))
        .slice(0, 30);

      const previewWatches: SearchResult[] = [];
      const availableModels = [...sortedModels];

      while (
        previewWatches.length < EXACT_RESULTS &&
        availableModels.length > 0
      ) {
        const randomIndex = Math.floor(Math.random() * availableModels.length);
        const selected = availableModels[randomIndex];

        if (!previewWatches.some((watch) => watch.symbol === selected.symbol)) {
          previewWatches.push(selected);
        }
        availableModels.splice(randomIndex, 1);
      }

      await fetchRecentPriceDataForResults(previewWatches);
      return previewWatches;
    } catch (error) {
      console.error('Error fetching watch data:', error);
      return [];
    }
  }, [fetchRecentPriceDataForResults]);

  const fetchPriceHistory = useCallback(
    async (symbol: string): Promise<ChartDataPoint[]> => {
      try {
        // Calculate 5 years ago timestamp for selected watch
        const now = Date.now();
        const fiveYearsAgo = now - 5 * 365 * 24 * 60 * 60 * 1000;
        const fromTimestamp = Math.floor(fiveYearsAgo / 1000);
        const toTimestamp = Math.floor(now / 1000);

        // Get the available data range
        const rangeResponse = await fetch(
          `/api/trading-dashboard/getDataRange?symbol=${symbol}&source=forum`
        );

        if (!rangeResponse.ok) return [];

        const range = await rangeResponse.json();

        // Use either 5 years ago or the earliest available data point, whichever is more recent
        const earliestTimestamp = Math.max(
          fromTimestamp,
          range.firstDataPoint || fromTimestamp
        );

        const response = await fetch(
          `/api/trading-dashboard/getBars?symbol=${symbol}&from=${earliestTimestamp}&to=${range.lastDataPoint || toTimestamp}&source=forum`
        );

        if (!response.ok) return [];

        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) return [];

        const validPoints: ChartDataPoint[] = [];
        for (const item of data) {
          let time = item.t || item.time;
          let price: number | null = null;

          if (typeof item.p === 'number') price = item.p;
          else if (typeof item.close === 'number') price = item.close;
          else if (typeof item.p === 'string') price = parseFloat(item.p);
          else if (typeof item.close === 'string')
            price = parseFloat(item.close);
          else if (typeof item.open === 'number') price = item.open;

          if (time && price !== null && !isNaN(price) && price > 0) {
            validPoints.push({ time, price });
          }
        }

        return validPoints.sort((a, b) => a.time - b.time);
      } catch (error) {
        console.error(`Error fetching price history for ${symbol}:`, error);
        return [];
      }
    },
    []
  );

  return {
    miniCharts,
    trends,
    prices,
    fetchWatchData,
    fetchPriceHistory,
    fetchRecentPriceDataForResults,
  };
};

const HeroSearch: React.FC<HeroSearchProps> = ({
  onSymbolSelect,
  datafeed,
  skipInitialLoad = false,
}) => {
  // Simplified state - removed animation-related state
  const [state, setState] = useState<HeroSearchState>({
    searchResults: [],
    isSearchVisible: false,
    inputValue: '',
    isLoading: false,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  // Use custom hook for data fetching
  const {
    miniCharts,
    trends,
    prices,
    fetchWatchData,
    fetchPriceHistory,
    fetchRecentPriceDataForResults,
  } = useWatchData(datafeed);

  // useRef to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);

  // Simple utility functions
  const getModelNumber = useCallback(
    (symbol: string): string => symbol.split(':')[1],
    []
  );

  const getFormattedWatchName = useCallback((result: SearchResult): string => {
    const brand = result.symbol.split(':')[0];
    const model = result.symbol.split(':')[1];
    return result.subModel
      ? `${brand} ${result.subModel} ${model}`
      : `${brand} ${model}`;
  }, []);

  // Handle symbol selection
  const handleSymbolSelect = useCallback(
    (result: SearchResult) => {
      onSymbolSelect(result);
      setState((prev) => ({
        ...prev,
        inputValue: getFormattedWatchName(result),
        isSearchVisible: false,
      }));
    },
    [onSymbolSelect, getFormattedWatchName]
  );

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!datafeed) {
          console.warn('Datafeed is null - using cached results');
          return;
        }

        if (!datafeed.searchSymbols) {
          console.error('Datafeed missing searchSymbols method');
          return;
        }

        // Skip empty queries to reduce API calls
        if (query.trim() === '') {
          setState((prev) => ({
            ...prev,
            isLoading: false,
          }));
          return;
        }

        setState((prev) => ({ ...prev, isLoading: true }));

        try {
          datafeed.searchSymbols(
            query,
            '',
            '',
            async (symbols: SearchResult[]) => {
              // Filter out non-watch symbols
              const watchOnlySymbols = symbols.filter((item) =>
                isWatchSymbol(item.symbol)
              );

              // Deduplicate search results
              const uniqueSymbols = Array.from(
                new Map(
                  watchOnlySymbols.map((item) => [item.symbol, item])
                ).values()
              );

              const processedSymbols = uniqueSymbols.slice(0, EXACT_RESULTS);

              setState((prev) => ({
                ...prev,
                searchResults: processedSymbols,
                isLoading: false,
              }));

              await fetchRecentPriceDataForResults(processedSymbols);
            }
          );
        } catch (error) {
          console.error('Search error:', error);
          setState((prev) => ({
            ...prev,
            searchResults: [],
            isLoading: false,
          }));
        }
      }, 300),
    [datafeed, fetchRecentPriceDataForResults]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setState((prev) => ({ ...prev, inputValue: value }));
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Update the useEffect to set initial search results
  useEffect(() => {
    const loadInitialData = async () => {
      if (initialDataFetchedRef.current) {
        // Already loaded data, don't load again
        return;
      }

      const results = await fetchWatchData();
      if (results && results.length > 0) {
        setState((prev) => ({
          ...prev,
          searchResults: results,
        }));
        initialDataFetchedRef.current = true;
      }
    };

    // Always fetch initial data, even if skipInitialLoad is true
    loadInitialData();
  }, [fetchWatchData]);

  // Simple render without animations
  return (
    <div className="relative w-full md:w-96">
      <Input
        ref={inputRef}
        className="w-full bg-slate-800/50 backdrop-blur-sm border border-slate-700/50
                   rounded-xl px-4 py-2 text-sm text-slate-200
                   hover:border-blue-500/30 focus:ring-2 focus:ring-blue-500/20"
        placeholder={INITIAL_PLACEHOLDER}
        value={state.inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          setState((prev) => ({ ...prev, isSearchVisible: true }));
        }}
      />

      {/* Search results without animations */}
      {state.isSearchVisible &&
        (state.searchResults.length > 0 || state.isLoading) && (
          <div className="absolute w-full mt-2 z-50">
            <Card className="bg-slate-800/90 backdrop-blur-xl border-slate-700/50 shadow-2xl rounded-xl overflow-hidden">
              {state.isLoading ? (
                <div className="p-4 text-center text-slate-400">
                  Searching...
                </div>
              ) : (
                <ul className="divide-y divide-slate-700/30">
                  {state.searchResults.map((result) => (
                    <SearchResultItem
                      key={result.symbol}
                      result={result}
                      miniCharts={miniCharts}
                      trends={trends}
                      prices={prices}
                      onSelect={handleSymbolSelect}
                      getFormattedWatchName={getFormattedWatchName}
                      getModelNumber={getModelNumber}
                    />
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
    </div>
  );
};

export default React.memo(HeroSearch);
